import { AxiosError } from 'axios';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { authApi, optimizationApi, campaignsApi } from '@/services/api';
import { metaAdsApi } from '@/services/api/metaAds';
import { SESSION_KEYS } from '@/config/constants';
import { FEEDBACK_CONFIG } from '@/config/feedback';
import { debug } from '@/utils/debug';
import { validateToken } from '@/utils/jwt';
import { clearAuthSession } from '@/utils/session';
import apiClient from '@/services/api/client';
import { queryClient } from '@/providers/QueryProvider';
import { features } from '@/config/features';
import { useDebugStore } from '@/store/debugStore';
import type { LoginCredentials, AuthState, User } from '@/types/auth';

// Per-session caches that must not survive a transition to a new
// authenticated identity (shared-browser / re-login / fresh-signup cases).
function clearStaleSession() {
  Object.keys(sessionStorage).forEach(key => {
    if (key.startsWith(SESSION_KEYS.CACHE_PREFIX)) {
      sessionStorage.removeItem(key);
    }
  });
  Object.values(FEEDBACK_CONFIG.STORAGE_KEYS).forEach(key => {
    sessionStorage.removeItem(key);
  });
  queryClient.clear();
}

interface AuthStore extends AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  linkedAccountsReady: boolean;
  hasLinkedGoogleAccounts: boolean;
  linkedAccountDetails: Array<{
    id: string;
    name: string;
    /** Phase 0.5 — ISO 4217 currency code captured at link time.
     *  Read by `usePoolCurrency` for the cross-platform currency guard.
     *  Null/undefined for legacy rows linked before the column was
     *  populated (pre-Phase 0.5 MCC fix). */
    currencyCode?: string | null;
  }>;
  hasLinkedMetaAccounts: boolean;
  linkedMetaAccountDetails: Array<{
    id: string;
    businessName: string | null;
    /** Phase 0.5 — ISO 4217 currency code captured at OAuth link time.
     *  Read by `usePoolCurrency` for the cross-platform currency guard. */
    currencyCode: string | null;
    // A2.3 §4.2 — token expiry data plumbed from /meta/accounts-summary so
    // MetaTokenExpiringBanner can read directly from the auth store
    // without a separate fetch on Home mount.
    tokenExpiringSoon: boolean;
    tokenExpiresAt: string | null;
    // A4.3.2 — default Facebook Page ID linked at OAuth time. Read by
    // CreateAdSetWizard's LEADS path to skip the page-picker UI when a
    // default exists. Null when the user didn't pick one at link time.
    pageId: string | null;
    // Migration 013 — Meta display name from /me?fields=name (public_profile)
    // and a runtime-derived Graph picture URL. Both are nullable for
    // pre-013 rows pending backfill. Read by the `MetaIdentityChip`
    // (Home page header) so the user can verify which Meta identity
    // they linked as.
    metaUserName: string | null;
    metaUserPictureUrl: string | null;
  }>;

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  checkExistingAuth: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  preloadLinkedAccounts: (memberUuid: string) => Promise<void>;
  setAuthFromGoogle: (token: string, memberUuid: string) => Promise<void>;
  setAuthFromSignup: (token: string, memberUuid: string, email: string) => void;
  refreshUserData: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      token: null,
      memberUuid: null,
      isAuthenticated: false,
      user: null,
      isLoading: false,
      error: null,
      linkedAccountsReady: false,
      hasLinkedGoogleAccounts: false,
      linkedAccountDetails: [],
      hasLinkedMetaAccounts: false,
      linkedMetaAccountDetails: [],

      // Actions
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      preloadLinkedAccounts: async (memberUuid: string) => {
        debug.log('AuthStore: Preloading linked accounts during login');

        const googlePromise = (async () => {
          try {
            // Combined endpoint that verifies accounts with the Google Ads
            // MCC API and returns names alongside the active-or-not flag.
            const response = await apiClient.get('/google-ads/accounts-summary');
            const { hasActiveAccounts, accounts } = response.data;
            // Backend returns `currency_code` (snake_case) per account; map to
            // the camelCase shape `usePoolCurrency` reads. Keep `id` + `name`
            // legacy shape intact for callers that don't care about currency.
            const linkedAccountDetails = (accounts || []).map((acc: {
              id: string; name: string; currency_code?: string | null;
            }) => ({
              id: acc.id,
              name: acc.name,
              currencyCode: acc.currency_code ?? null,
            }));
            return {
              hasLinkedGoogleAccounts: Boolean(hasActiveAccounts) && accounts?.length > 0,
              linkedAccountDetails,
            };
          } catch (error: unknown) {
            debug.error('AuthStore: Failed to preload Google accounts', error);
            return { hasLinkedGoogleAccounts: false, linkedAccountDetails: [] };
          }
        })();

        const metaPromise = (async () => {
          try {
            const accounts = await metaAdsApi.getLinkedAccountsSummary();
            const linkedMetaAccountDetails = accounts.map((acc) => ({
              id: acc.meta_ad_account_id,
              businessName: acc.business_name,
              // Phase 0.5 — currency captured at OAuth link time, surfaced
              // for the usePoolCurrency cross-platform guard.
              currencyCode: acc.account_currency ?? null,
              // Plumb expiry through so MetaTokenExpiringBanner can read it
              // off the auth store; backend fills these in
              // /meta/accounts-summary per A2.3 §4.2.
              tokenExpiringSoon: acc.token_expiring_soon ?? false,
              tokenExpiresAt: acc.token_expires_at ?? null,
              // A4.3.2 — default page_id (LEADS ad-set picker shortcut).
              pageId: acc.page_id ?? null,
              // Migration 013 — public_profile-sourced identity for the
              // linked-account chip. Both nullable for legacy rows.
              metaUserName: acc.meta_user_name ?? null,
              metaUserPictureUrl: acc.meta_user_picture_url ?? null,
            }));
            return {
              hasLinkedMetaAccounts: linkedMetaAccountDetails.length > 0,
              linkedMetaAccountDetails,
            };
          } catch (error: unknown) {
            // 403 is the expected response for non-allowlisted members
            // (Phase A1 gate per META_INTEGRATION_PLAN.md §16.0). Swallow
            // silently; other errors degrade gracefully but get logged.
            const status =
              error instanceof AxiosError ? error.response?.status : undefined;
            if (status !== 403) {
              debug.error('AuthStore: Failed to preload Meta accounts', error);
            }
            return { hasLinkedMetaAccounts: false, linkedMetaAccountDetails: [] };
          }
        })();

        const [google, meta] = await Promise.all([googlePromise, metaPromise]);

        debug.log('AuthStore: Accounts summary received', {
          googleCount: google.linkedAccountDetails.length,
          metaCount: meta.linkedMetaAccountDetails.length,
        });

        set({
          linkedAccountsReady: true,
          ...google,
          ...meta,
        });
      },

      login: async (credentials: LoginCredentials) => {
        debug.log('AuthStore: Starting login for', credentials.username);
        set({ isLoading: true, error: null, linkedAccountsReady: false });

        try {
          debug.log('AuthStore: Clearing stale cache before login');
          clearStaleSession();

          const response = await authApi.login(credentials);

          // Store auth data in localStorage (persists across tabs)
          localStorage.setItem(SESSION_KEYS.AUTH_TOKEN, response.access_token);
          localStorage.setItem(SESSION_KEYS.MEMBER_UUID, response.member_uuid);

          // Fetch full user details
          let userDetails: User = {
            id: response.member_uuid,
            email: credentials.username,
            memberUuid: response.member_uuid,
          };

          try {
            const userResponse = await apiClient.get('/auth/user');
            const userData = userResponse.data;
            userDetails = {
              id: userData.member_uuid,
              email: userData.email,
              memberUuid: userData.member_uuid,
              firstName: userData.first_name,
              lastName: userData.last_name,
              companyName: userData.company_name,
            };
            debug.log('AuthStore: User details fetched', userDetails);
          } catch (error) {
            debug.warn('AuthStore: Failed to fetch user details', error);
          }

          // Update store state
          set({
            token: response.access_token,
            memberUuid: response.member_uuid,
            isAuthenticated: true,
            user: userDetails,
          });

          // Preload linked accounts before completing login
          await get().preloadLinkedAccounts(response.member_uuid);

          set({ isLoading: false, error: null });

          debug.log('AuthStore: Login successful, cache cleared, accounts preloaded');
        } catch (error: unknown) {
          debug.error('AuthStore: Login failed', error);

          // Handle specific error types
          let errorMessage = 'Login failed';
          const apiError = error as {
            code?: string;
            message?: string;
            response?: { data?: { detail?: string } }
          };

          if (apiError.code === 'ECONNABORTED' || apiError.message?.includes('timeout')) {
            errorMessage = 'Login timeout. The server is taking too long to respond. Please try again.';
          } else if (apiError.response?.data?.detail) {
            errorMessage = apiError.response.data.detail;
          } else if (apiError.message) {
            errorMessage = apiError.message;
          }

          set({
            isLoading: false,
            error: errorMessage,
            token: null,
            memberUuid: null,
            isAuthenticated: false,
            user: null,
            linkedAccountsReady: false,
          });
          throw error;
        }
      },

      logout: () => {
        debug.log('AuthStore: Logging out');

        // Clear session storage
        clearAuthSession();

        // Clear React Query cache to prevent stale data on next login
        queryClient.clear();

        // Drop debug-store state so API logs, optimization history, and
        // performance metrics from this member do not bleed into the next
        // session on a shared browser.
        useDebugStore.getState().clearAll();

        // Reset store state
        set({
          token: null,
          memberUuid: null,
          isAuthenticated: false,
          user: null,
          error: null,
          isLoading: false,
          linkedAccountsReady: false,
          hasLinkedGoogleAccounts: false,
          linkedAccountDetails: [],
          hasLinkedMetaAccounts: false,
          linkedMetaAccountDetails: [],
        });
      },

      clearError: () => {
        set({ error: null });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      refreshUserData: async () => {
        const state = get();
        if (!state.isAuthenticated || !state.memberUuid) {
          return;
        }

        try {
          debug.log('AuthStore: Refreshing user data');
          const userResponse = await apiClient.get('/auth/user');
          const userData = userResponse.data;
          const userDetails: User = {
            id: userData.member_uuid,
            email: userData.email,
            memberUuid: userData.member_uuid,
            firstName: userData.first_name,
            lastName: userData.last_name,
            companyName: userData.company_name,
          };
          set({ user: userDetails });
          debug.log('AuthStore: User data refreshed', userDetails);
        } catch (error) {
          debug.warn('AuthStore: Failed to refresh user data', error);
        }
      },

      setAuthFromGoogle: async (token: string, memberUuid: string) => {
        debug.log('AuthStore: Setting auth from Google Sign-In');
        set({ isLoading: true, error: null, linkedAccountsReady: false });

        try {
          debug.log('AuthStore: Clearing stale cache before Google auth');
          clearStaleSession();

          // Store auth data in localStorage (persists across tabs)
          localStorage.setItem(SESSION_KEYS.AUTH_TOKEN, token);
          localStorage.setItem(SESSION_KEYS.MEMBER_UUID, memberUuid);

          // Fetch full user details
          let userDetails: User = {
            id: memberUuid,
            email: '',
            memberUuid: memberUuid,
          };

          try {
            const userResponse = await apiClient.get('/auth/user');
            const userData = userResponse.data;
            userDetails = {
              id: userData.member_uuid,
              email: userData.email,
              memberUuid: userData.member_uuid,
              firstName: userData.first_name,
              lastName: userData.last_name,
              companyName: userData.company_name,
            };
            debug.log('AuthStore: User details fetched after Google auth', userDetails);
          } catch (error) {
            debug.warn('AuthStore: Failed to fetch user details after Google auth', error);
          }

          // Update store state
          set({
            token: token,
            memberUuid: memberUuid,
            isAuthenticated: true,
            user: userDetails,
          });

          // Preload linked accounts
          await get().preloadLinkedAccounts(memberUuid);

          // Fire-and-forget prefetches - don't block Google auth completion
          // Data loads in background while user sees home page
          if (features.campaignDataFreshness === 'refresh_on_login') {
            debug.log('AuthStore: Starting background prefetches for Google auth');

            // Prefetch campaigns (non-blocking)
            queryClient.prefetchQuery({
              queryKey: ['campaigns', false],
              queryFn: () => campaignsApi.getCampaigns(false),
              staleTime: Infinity,
            });

            // Prefetch app configuration (non-blocking)
            queryClient.prefetchQuery({
              queryKey: ['app-config', memberUuid],
              queryFn: () => optimizationApi.loadConfiguration(memberUuid),
              staleTime: Infinity,
            });
          }

          set({ isLoading: false, error: null });
          debug.log('AuthStore: Google auth successful, cache cleared, accounts preloaded, prefetches started');
        } catch (error: unknown) {
          debug.error('AuthStore: Google auth setup failed', error);
          set({
            isLoading: false,
            error: 'Failed to complete Google sign-in',
            token: null,
            memberUuid: null,
            isAuthenticated: false,
            user: null,
            linkedAccountsReady: false,
          });
          throw error;
        }
      },

      setAuthFromSignup: (token: string, memberUuid: string, email: string) => {
        debug.log('AuthStore: Setting auth from signup');
        clearStaleSession();

        // Persist to localStorage — this is what apiClient's request
        // interceptor reads. Writing to sessionStorage here would leave
        // the next request without an Authorization header, get a 401,
        // and trip the global logout interceptor.
        localStorage.setItem(SESSION_KEYS.AUTH_TOKEN, token);
        localStorage.setItem(SESSION_KEYS.MEMBER_UUID, memberUuid);

        set({
          token,
          memberUuid,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          // Reset per-identity fields so state from a prior signed-in user
          // on this device can't leak into the new session. A fresh signup
          // has zero linked accounts; no preload needed.
          linkedAccountsReady: false,
          hasLinkedGoogleAccounts: false,
          linkedAccountDetails: [],
          hasLinkedMetaAccounts: false,
          linkedMetaAccountDetails: [],
          user: {
            id: memberUuid,
            email,
            memberUuid,
            isActive: true,
          },
        });
      },

      checkExistingAuth: async () => {
        debug.log('AuthStore: Checking existing authentication');
        set({ isLoading: true });

        const token = localStorage.getItem(SESSION_KEYS.AUTH_TOKEN);
        const memberUuid = localStorage.getItem(SESSION_KEYS.MEMBER_UUID);
        debug.log('AuthStore: Auth data from localStorage', { hasToken: !!token, hasMemberUuid: !!memberUuid });

        if (token && memberUuid) {
          // Validate JWT token format and expiration
          const validation = validateToken(token);

          debug.log('AuthStore: Token validation', {
            valid: validation.valid,
            expired: validation.expired,
            expiresIn: validation.expiresIn,
          });

          if (validation.valid && !validation.expired) {
            // Invalidate React Query cache to trigger fresh data fetches
            // Using invalidateQueries() instead of clear() keeps cached data visible
            // while fresh data loads in the background (prevents flash of empty/zero data)
            debug.log('AuthStore: Invalidating React Query cache on session restore');
            queryClient.invalidateQueries();

            // Fetch full user details
            let userDetails: User = {
              id: memberUuid,
              email: '',
              memberUuid,
            };

            try {
              const userResponse = await apiClient.get('/auth/user');
              const userData = userResponse.data;
              userDetails = {
                id: userData.member_uuid,
                email: userData.email,
                memberUuid: userData.member_uuid,
                firstName: userData.first_name,
                lastName: userData.last_name,
                companyName: userData.company_name,
              };
              debug.log('AuthStore: User details fetched on session restore', userDetails);
            } catch (error) {
              debug.warn('AuthStore: Failed to fetch user details on session restore', error);
            }

            // Token is valid and not expired
            set({
              token,
              memberUuid,
              isAuthenticated: true,
              user: userDetails,
              isLoading: false,
            });
            debug.log('AuthStore: Existing authentication restored', {
              expiresIn: `${Math.floor(validation.expiresIn / 60)} minutes`,
            });

            // Check for linked accounts after restoring session
            await get().preloadLinkedAccounts(memberUuid);

            // Prefetch campaigns and app-config data (same as login flow)
            // This ensures data is available after page refresh
            if (features.campaignDataFreshness === 'refresh_on_login') {
              debug.log('AuthStore: Starting background prefetches on session restore');

              // Prefetch campaigns (non-blocking)
              queryClient.prefetchQuery({
                queryKey: ['campaigns', false],
                queryFn: () => campaignsApi.getCampaigns(false),
                staleTime: Infinity,
              });

              // Prefetch app configuration (non-blocking)
              queryClient.prefetchQuery({
                queryKey: ['app-config', memberUuid],
                queryFn: () => optimizationApi.loadConfiguration(memberUuid),
                staleTime: Infinity,
              });
            }
          } else {
            // Token is expired or invalid - clear session
            debug.log('AuthStore: Token expired or invalid, clearing session');
            clearAuthSession();

            set({
              token: null,
              memberUuid: null,
              isAuthenticated: false,
              user: null,
              isLoading: false,
            });
          }
        } else {
          debug.log('AuthStore: No existing authentication found');
          set({ isLoading: false });
        }
      },
    }),
    {
      name: 'auth-store',
    }
  )
);